/**
 * Email utility functions for parsing and formatting IMAP messages
 */

/**
 * Parses an IMAP message object and extracts clean, structured data
 * @param {Object} message - The IMAP message object
 * @returns {Object} Parsed email data
 */
export const parseEmailMessage = (message) => {
  if (!message) return null;

  const { messageText, rawPayload, sender, recipient, timestamp, attachments } = message;

  // Extract subject from rawPayload (preferred) or messageText
  let subject = '';
  let body = '';

  if (rawPayload?.subject) {
    subject = rawPayload.subject;
  }

  // Parse messageText to extract body
  if (messageText) {
    // Check if messageText contains "Subject:" prefix
    const subjectMatch = messageText.match(/^Subject:\s*(.+?)(?:\n\n|\n)/);

    if (subjectMatch) {
      // If subject wasn't found in rawPayload, extract it from messageText
      if (!subject) {
        subject = subjectMatch[1].trim();
      }
      // Extract body (everything after "Subject: ... \n\n")
      body = messageText.substring(subjectMatch[0].length).trim();
    } else {
      // If no subject prefix, entire messageText is the body
      body = messageText.trim();
    }
  }

  // Extract sender information
  const senderInfo = {
    email: sender?.id || rawPayload?.from?.value?.[0]?.address || '',
    name: sender?.name || rawPayload?.from?.value?.[0]?.name || '',
  };

  // Extract recipient information
  const recipientInfo = {
    email: recipient?.id || rawPayload?.to?.value?.[0]?.address || '',
    name: recipient?.name || rawPayload?.to?.value?.[0]?.name || '',
  };

  // Parse date
  const date = rawPayload?.date ? new Date(rawPayload.date) : new Date(timestamp);

  return {
    subject,
    body,
    sender: senderInfo,
    recipient: recipientInfo,
    date,
    timestamp,
    messageId: message.messageId,
    attachments: attachments || [],
    isRead: message.isRead,
    rawHeaders: rawPayload?.headers || {},
  };
};

/**
 * Formats email sender for display
 * @param {Object} sender - Sender object with name and email
 * @returns {string} Formatted sender string
 */
export const formatEmailSender = (sender) => {
  if (!sender) return 'Unknown Sender';

  if (sender.name && sender.name.includes('<') && sender.name.includes('>')) {
    const emailMatch = sender.name.match(/<([^>]+)>/);
    if (emailMatch) {
      return emailMatch[1];
    }
  }

  if (sender.email) {
    return sender.email;
  }

  if (sender.name) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailPattern.test(sender.name)) {
      return sender.name;
    }
    const emailInNameMatch = sender.name.match(/([^\s@]+@[^\s@]+\.[^\s@]+)/);
    if (emailInNameMatch) {
      return emailInNameMatch[1];
    }
  }

  return sender.name || 'Unknown Sender';
};

/**
 * Formats email recipient for display
 * @param {Object} recipient - Recipient object with name and email
 * @returns {string} Formatted recipient string
 */
export const formatEmailRecipient = (recipient) => {
  if (!recipient) return 'Unknown Recipient';

  if (recipient.name && recipient.name.includes('<') && recipient.name.includes('>')) {
    const emailMatch = recipient.name.match(/<([^>]+)>/);
    if (emailMatch) {
      return emailMatch[1];
    }
  }

  if (recipient.email) {
    return recipient.email;
  }

  if (recipient.name) {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailPattern.test(recipient.name)) {
      return recipient.name;
    }
    const emailInNameMatch = recipient.name.match(/([^\s@]+@[^\s@]+\.[^\s@]+)/);
    if (emailInNameMatch) {
      return emailInNameMatch[1];
    }
  }

  return recipient.name || 'Unknown Recipient';
};

/**
 * Extracts plain text from email body (removes excessive whitespace and formatting)
 * @param {string} body - Email body text
 * @returns {string} Cleaned body text
 */
export const cleanEmailBody = (body) => {
  if (!body) return '';

  // Remove excessive newlines (more than 2 consecutive)
  let cleaned = body.replace(/\n{3,}/g, '\n\n');

  // Trim leading/trailing whitespace
  cleaned = cleaned.trim();

  return cleaned;
};

/**
 * Checks if an email has attachments
 * @param {Object} message - The email message object
 * @returns {boolean} True if message has attachments
 */
export const hasAttachments = (message) => {
  return (
    message?.attachments &&
    Array.isArray(message.attachments) &&
    message.attachments.length > 0
  );
};

/**
 * Gets a short preview of the email body for list display
 * @param {string} body - Email body text
 * @param {number} maxLength - Maximum length of preview
 * @returns {string} Preview text
 */
export const getEmailPreview = (body, maxLength = 100) => {
  if (!body) return '';

  const cleaned = cleanEmailBody(body);

  if (cleaned.length <= maxLength) {
    return cleaned;
  }

  return cleaned.substring(0, maxLength).trim() + '...';
};

/**
 * Detects if email body contains URLs and makes them clickable
 * @param {string} text - Email body text
 * @returns {Array} Array of text segments and links
 */
export const parseEmailLinks = (text) => {
  if (!text) return [];

  // URL regex pattern
  const urlPattern = /(https?:\/\/[^\s]+)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = urlPattern.exec(text)) !== null) {
    // Add text before URL
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex, match.index),
      });
    }

    // Add URL
    parts.push({
      type: 'link',
      content: match[0],
      url: match[0],
    });

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.substring(lastIndex),
    });
  }

  return parts.length > 0 ? parts : [{ type: 'text', content: text }];
};

/**
 * Formats attachment file size
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatAttachmentSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};
